import { LoaderCircle, PlugZap, RotateCw, ServerCrash, ShieldAlert, TriangleAlert } from "lucide-react";
import { useRuntime } from "@/app/runtime";
import { HowToStartContent } from "@/app/shell/HowToStartDialog";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/common/CodeBlock";
import { REQUIRED_API_VERSION } from "@/lib/buildInfo";
import { PermissionBlockedContent } from "./PermissionBlockedDialog";

export function RuntimeStatePage() {
  const r = useRuntime();
  const wrap = (title: string, body: React.ReactNode, Icon = PlugZap) => (
    <section className="mx-auto mt-12 flex max-w-2xl flex-col gap-4">
      <Icon aria-hidden className="size-10 text-muted-foreground" />
      <h1 className="text-2xl font-semibold">{title}</h1>
      {body}
    </section>
  );
  switch (r.state) {
    case "not_running":
      return wrap("Start the ATS runtime", <HowToStartContent />);
    case "starting":
      return wrap(
        "Starting the runtime…",
        <p className="flex items-center gap-2 text-muted-foreground">
          <LoaderCircle aria-hidden className="animate-spin" /> This page continues by itself when ATS is ready.
        </p>,
        LoaderCircle,
      );
    case "port_conflict":
      return wrap(
        "Another program is using port 7050",
        <>
          <p>ATS always listens on port 7050 and never picks another port. Find the program holding it, stop it, then start ATS again.</p>
          <CodeBlock code="ss -ltnp | grep 7050" copyLabel="Copy command" />
        </>,
        TriangleAlert,
      );
    case "permission_blocked":
      return wrap("Allow ATS to reach your runtime", <PermissionBlockedContent onRetry={r.refetch} />, ShieldAlert);
    case "incompatible":
      return wrap(
        "Runtime update needed",
        <p>
          This UI needs runtime API {REQUIRED_API_VERSION}; the runtime on this machine is {r.version?.runtime_version ?? "unknown"} with
          API {r.version?.api_version ?? "?"}. Install the latest ATS runtime.
        </p>,
        TriangleAlert,
      );
    case "unhealthy":
      return wrap(
        "Runtime unhealthy",
        <>
          <p className="text-muted-foreground">{r.health?.degraded_reason ?? "The runtime reported a problem."}</p>
          <Button variant="outline" onClick={r.refetch} className="self-start">
            <RotateCw aria-hidden /> Retry
          </Button>
        </>,
        ServerCrash,
      );
    default:
      return null;
  }
}
